// [UNTAPPED-v2] Sidebar - Contains navigation link to Untapped Opportunities
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Home,
    Users,
    Search,
    Eye,
    TrendingUp,
    BookOpen,
    BarChart3,
    Phone,
    MessageSquare,
    HelpCircle,
    FileText,
    Settings,
    User,
    CreditCard,
    Target,
    Mail,
    Box,
    ChevronRight,
    ChevronDown,
    Edit,
    UserCog,
    PlusCircle,
    Radar,
    FileCheck,
    LayoutDashboard,
    Terminal,
    Trash2,
    Share2,
    Bot
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const Sidebar = ({
sidebarCollapsed = false,
setSidebarCollapsed = () => {},
openMenus = {},
toggleMenu = () => {}
}) => {
const navigate = useNavigate();
const location = useLocation();
const [businessEmail, setBusinessEmail] = useState(null);
const [loading, setLoading] = useState(true);
const { isDark } = useTheme();

// Fetch business email on component mount
useEffect(() => {
    const fetchBusinessEmail = async() => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
            const response = await fetch(`${apiUrl}/api/sessionValue`, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success) {
                    setBusinessEmail(result.data.businessEmail);
                }
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    fetchBusinessEmail();
}, []);

// Check if user should see admin options (Update Brand and User Panel)
const showAdminOptions = businessEmail?.trim().toLowerCase() === 'mars.mediacle@gmail.com';

const baseMenuItems = [{
        key: 'dashboard',
        label: 'Dashboard',
        icon: Home,
        link: '/dashboard',
        route: '/dashboard'
    },
    {
        key: 'affiliate-recruitment',
        label: 'Affiliate Recruitment',
        icon: Users,
        link: '/affiliate-recruitment',
        route: '/affiliate-recruitment'
    },
    {
        key: 'competitorResearch',
        label: 'Competitor Research',
        icon: Search,
        hasSubmenu: true,
        isOpen: openMenus.competitorResearch || false,
        submenu: [
            { key: 'competitor-spy', label: 'Competitor Spy', icon: Eye, link: '/competitor-spy', route: '/competitor-spy' },
            { key: 'affiliate-gap', label: 'Affiliate Gap', icon: TrendingUp, link: '/affiliate-gap', route: '/affiliate-gap' }
        ]
    },
    {
        key: 'affiliate-directory',
        label: 'Affiliate Directory',
        icon: BookOpen,
        link: '/affiliate-directory',
        route: '/affiliate-directory'
    },
    {
        key: 'crm-pipeline',
        label: 'Affiliate Pipeline',
        icon: LayoutDashboard,
        link: '/crm-pipeline',
        route: '/crm-pipeline'
    },
    {
        key: 'position-tracker',
        label: 'Position Tracker',
        icon: Target,
        link: '/position-tracker',
        route: '/position-tracker'
    },
    // {
    //     key: 'terms-checker',
    //     label: 'Terms Checker',
    //     icon: FileCheck,
    //     link: '/terms-checker',
    //     route: '/terms-checker'
    // },
    {
        key: 'keyword-explorer',
        label: 'Keywords Explorer',
        icon: BarChart3,
        link: '/keyword-explorer',
        route: '/keyword-explorer'
    },
    {
        key: 'serp-checker',
        label: 'SERP Checker',
        icon: TrendingUp,
        link: '/SerpChecker',
        route: '/SerpChecker'
    },

    // {
    //     key: 'sand-box',
    //     label: 'Sand Box',
    //     icon: Box,
    //     link: '/sand-box',
    //     route: '/sand-box'
    // },
    {
        key: 'contact-finder',
        label: 'Contacts Finder',
        icon: Phone,
        link: '/contact-finder',
        route: '/contact-finder'
    },
    {
        key: 'social-media-finder',
        label: 'Social Media Finder',
        icon: Share2,
        link: '/social-media-finder',
        route: '/social-media-finder'
    },
    {
        key: 'messages',
        label: 'Messages',
        icon: MessageSquare,
        hasSubmenu: true,
        isOpen: openMenus.messages || false,
        submenu: [
            { key: 'emails-sent', label: 'Inbox', icon: Mail, link: '/sent-box', route: '/sent-box' },
            // AI Approval is gated to mars.mediacle@gmail.com only — same flag
            // used elsewhere for admin-only entries.
            ...(showAdminOptions ? [{ key: 'ai-approval', label: 'AI Approval', icon: FileCheck, link: '/ai-approval', route: '/ai-approval' }] : []),
            { key: 'email-settings', label: 'Email Settings', icon: Settings, link: '/email-settings', route: '/email-settings' }
        ]
    },
    {
        key: 'support',
        label: 'Support',
        icon: HelpCircle,
        hasSubmenu: true,
        isOpen: openMenus.support || false,
        submenu: [
            { key: 'help', label: 'Help', icon: HelpCircle, link: 'mailto:mars@mediacle.com' },
            { key: 'documentation', label: 'Documentation', icon: FileText, link: '/documentation', route: '/documentation' }
        ]
    },
    {
        key: 'settings',
        label: 'Settings',
        icon: Settings,
        hasSubmenu: true,
        isOpen: openMenus.settings || false,
        submenu: [
            { key: 'profile', label: 'Profile', icon: User, link: '/profile', route: '/profile' },
            { key: 'plans', label: 'Plans & Billing', icon: CreditCard, link: '/pricing-plan', route: '/pricing-plan' }
        ]
    }
];

// Add admin options (Add Brand, Update Brand and User Panel) conditionally
const menuItems = showAdminOptions ? [
        ...baseMenuItems.slice(0, 3), // Dashboard, Affiliate Recruitment, Affiliate Recruitment Auto
        {
            key: 'brand-add',
            label: 'Add Brand',
            icon: PlusCircle,
            link: '/brand-add',
            route: '/brand-add'
        },
        {
            key: 'manual-domain-add',
            label: 'Manual Domain Add',
            icon: FileText,
            link: '/manual-domain-add',
            route: '/manual-domain-add'
        },
        {
            key: 'update-brand',
            label: 'Update Brand',
            icon: Edit,
            link: '/update-brand',
            route: '/update-brand'
        },
        {
            key: 'user-panel',
            label: 'User Panelss',
            icon: UserCog,
            link: '/user-panel',
            route: '/user-panel'
        },
        {
            key: 'affiliate-finder',
            label: 'Affiliate Finder',
            icon: Radar,
            link: '/affiliate-finder',
            route: '/affiliate-finder'
        },
        {
            key: 'affiliate-finder-nocost',
            label: 'Affiliate Finder (No Cost)',
            icon: Radar,
            link: '/affiliate-finder-nocost',
            route: '/affiliate-finder-nocost'
        },
        {
            key: 'affiliate-agent',
            label: 'Affiliate Agent',
            icon: Bot,
            link: '/affiliate-agent',
            route: '/affiliate-agent'
        },
        {
            key: 'manual-affiliate-add',
            label: 'Manual Add',
            icon: PlusCircle,
            link: '/manual-affiliate-add',
            route: '/manual-affiliate-add'
        },
        ...baseMenuItems.slice(2) // Rest of the menu items (after dashboard + affiliate-recruitment)
    ] :
    baseMenuItems;

// Function to handle menu item clicks
const handleMenuClick = (item) => {
    if (item.hasSubmenu) {
        toggleMenu(item.key);
    } else if (item.route) {
        navigate(item.route);
    } else if (item.link && item.link !== '#') {
        if (item.link.startsWith('mailto:')) {
            window.location.href = item.link;
        } else {
            navigate(item.link);
        }
    }
};

// Function to check if menu item is active
const isMenuItemActive = (item) => {
    if (item.route) {
        return location.pathname === item.route;
    }
    return false;
};

return ( <
    div className = { `fixed left-0 top-0 h-full bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border-r border-gray-200/60 dark:border-slate-700/60 z-40 transition-all duration-300 ${
      sidebarCollapsed ? '-translate-x-full md:translate-x-0 w-64 md:w-16' : 'translate-x-0 w-64'
    } overflow-hidden` } >

    { /* Sidebar Header - Ultra Clean */ } <
    div className = "h-14 flex items-center justify-center px-4 border-b border-gray-200/40 dark:border-slate-700/40" > {
        sidebarCollapsed ? ( <
            div className = "w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center shadow-sm" >
            <
            span className = "text-white font-semibold text-xs" > M < /span> < /
            div >
        ) : ( <
            div className = "flex items-center justify-center w-full" >
            <
            div className = "" >
            <
            img src = {isDark ? "/MARS-White.png" : "/MARS-Color.png"}
            alt = "MARS Logo"
            style = {{ width: '70%', display: 'block', margin: '0 auto' }} / >
            <
            /div> < /
            div >
        )
    } <
    /div>

    { /* Navigation - Floating Style */ } <
    div className = "p-3 h-full pb-32 overflow-y-auto scrollbar-thin" >
    <
    nav className = "space-y-0.5" > {
        menuItems.map((item) => {
                const isActive = isMenuItemActive(item);

                return ( <
                    div key = { item.key } > { /* Main Menu Item - Sleek Design */ } <
                    div className = { `relative flex items-center justify-between px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200 group ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-slate-200'
                  }` }
                    onClick = {
                        () => handleMenuClick(item)
                    } >
                    <
                    div className = "flex items-center gap-3 flex-1 min-w-0" >
                    <
                    item.icon className = { `w-4 h-4 flex-shrink-0 ${sidebarCollapsed ? 'mx-auto' : ''} ${
                      isActive ? 'text-white' : 'text-gray-500 dark:text-slate-500 group-hover:text-gray-700 dark:group-hover:text-slate-300'
                    }` }
                    /> {!sidebarCollapsed && ( <
                    span className = "font-medium text-sm truncate" > { item.label } < /span>
                )
            } <
            /div> {!sidebarCollapsed && item.hasSubmenu && ( <
            ChevronDown className = { `w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${
                      item.isOpen ? 'rotate-180' : ''
                    } ${isActive ? 'text-white/80' : 'text-gray-400'}` }
            />
        )
    }

    { /* Active Indicator */ } {
        isActive && ( <
            div className = "absolute right-1 top-1/2 transform -translate-y-1/2 w-1 h-4 bg-white/30 rounded-full" > < /div>
        )
    } <
    /div>

    { /* Submenu - Clean Expansion */ } {
        !sidebarCollapsed && item.hasSubmenu && ( <
            div className = { `overflow-hidden transition-all duration-300 ${
                    item.isOpen ? 'max-h-96 opacity-100 mt-1' : 'max-h-0 opacity-0'
                  }` } >
            <
            div className = "pl-6 pr-3 space-y-0.5" > {
                item.submenu.map((subItem) => ( <
                    Link key = { subItem.key }
                    to = { subItem.link }
                    className = "flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer text-gray-500 dark:text-slate-400 hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-gray-700 dark:hover:text-slate-200 transition-all duration-200 group" >
                    <
                    subItem.icon className = "w-3.5 h-3.5 text-gray-400 dark:text-slate-500 group-hover:text-gray-600 dark:group-hover:text-slate-300 flex-shrink-0" / >
                    <
                    span className = "text-sm truncate" > { subItem.label } < /span> < /
                    Link >
                ))
            } <
            /div> < /
            div >
        )
    } <
    /div>
);
})
} <
/nav> < /
div >

    { /* Footer - Minimal & Clean */ } <
    div className = {`absolute bottom-0 left-0 right-0 p-3 bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm border-t border-gray-200/40 dark:border-slate-700/40`} >

    {/* Admin-only: Test SES Email button */}
    {showAdminOptions && (
        <button
            onClick={async () => {
                try {
                    const btn = document.getElementById('test-ses-btn');
                    if (btn) { btn.textContent = 'Sending...'; btn.disabled = true; }
                    const basePath = process.env.REACT_APP_API_URL || process.env.API_BASE_URL || 'https://mars2.mediacle.net';
                    const res = await fetch(`${basePath}/api/live-affiliate-monitor/test-ses-email`, {
                        method: 'POST',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' }
                    });
                    const data = await res.json();
                    alert(data.success ? `Test emails sent to:\n${data.recipients.join('\n')}` : `Failed: ${data.message}`);
                } catch (err) {
                    alert('Failed to send test email: ' + err.message);
                } finally {
                    const btn = document.getElementById('test-ses-btn');
                    if (btn) { btn.textContent = sidebarCollapsed ? '' : 'Test SES'; btn.disabled = false; }
                }
            }}
            id="test-ses-btn"
            className={`w-full flex items-center gap-2 px-3 py-2 mb-2 rounded-lg text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors ${sidebarCollapsed ? 'justify-center' : ''}`}
            title="Send Test SES Email"
        >
            <Mail className="w-4 h-4 flex-shrink-0" />
            {!sidebarCollapsed && <span className="text-xs font-medium">Test SES</span>}
        </button>
    )}

    {!sidebarCollapsed && (
        <div className = "text-center" >
        <div className = "text-xs text-gray-400 dark:text-slate-500" > ©2026 MARS </div>
        </div>
    )}
    </div>
<
/div>
);
};

export default Sidebar;